import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { api } from "../../api/client";
import PortalSidebar from "../../components/portal/PortalSidebar";
import { 
  FileText, AlertCircle, Clock, CheckCircle, XCircle,
  Plus, ChevronRight, ChevronLeft, X, Loader2, User, Calendar,
  ShieldX, UserX, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

function getStatusColor(status) {
  const colors = {
    'OPEN': 'bg-blue-100 text-blue-700',
    'PROCESSING': 'bg-blue-100 text-blue-700',
    'IN_PROGRESS': 'bg-amber-100 text-amber-700',
    'COMPLETED': 'bg-emerald-100 text-emerald-700',
    'CANCELLED': 'bg-slate-100 text-slate-600',
    'PENDING': 'bg-yellow-100 text-yellow-700',
    'SUBMITTED': 'bg-slate-100 text-slate-600',
    'ERROR': 'bg-red-100 text-red-700',
    'ACCESS DENIED': 'bg-red-100 text-red-700',
    'NOT FOUND': 'bg-amber-100 text-amber-700',
    'TIMEOUT': 'bg-slate-100 text-slate-600',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
}

function getStatusIcon(status, isActivelyProcessing = false) {
  // PROCESSING with spinner only for actively in-flight requests
  if (isActivelyProcessing) return <Loader2 size={14} className="text-blue-600 animate-spin" />;
  
  if (status === 'COMPLETED') return <CheckCircle size={14} className="text-emerald-600" />;
  if (status === 'SUBMITTED') return <Clock size={14} className="text-slate-500" />;
  if (status === 'PROCESSING') return <Clock size={14} className="text-blue-600" />; // Stored PROCESSING (legacy)
  if (status === 'IN_PROGRESS') return <Clock size={14} className="text-amber-600" />;
  if (status === 'CANCELLED') return <XCircle size={14} className="text-slate-400" />;
  if (status === 'ERROR') return <XCircle size={14} className="text-red-600" />;
  if (status === 'ACCESS DENIED') return <ShieldX size={14} className="text-red-600" />;
  if (status === 'NOT FOUND') return <UserX size={14} className="text-amber-600" />;
  if (status === 'TIMEOUT') return <AlertTriangle size={14} className="text-slate-500" />;
  if (status === 'PENDING') return <Clock size={14} className="text-yellow-600" />;
  return <Clock size={14} className="text-slate-500" />;
}

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 15;
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [newRequest, setNewRequest] = useState({
    patient_first_name: '',
    patient_last_name: '',
    patient_dob: '',
    question: '',
    response_method: 'portal',
  });
  
  // Processing requests (keyed by request_id)
  const [processingRequests, setProcessingRequests] = useState({});
  
  // View response modal state
  const [viewingRequest, setViewingRequest] = useState(null);
  
  // Error popup state
  const [errorPopup, setErrorPopup] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", perPage);
      params.append("offset", (page - 1) * perPage);

      const res = await api.get(`/portal/requests?${params.toString()}`);
      setRequests(res.data.requests || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch requests:", err);
      setError("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!newRequest.patient_first_name.trim() || !newRequest.patient_last_name.trim()) {
      toast.error("Please enter patient first and last name");
      return;
    }
    if (!newRequest.patient_dob) {
      toast.error("Please enter patient date of birth");
      return;
    }
    if (!newRequest.question.trim()) {
      toast.error("Please enter your question");
      return;
    }
    
    // Generate a temporary ID for tracking
    const tempId = `temp-${Date.now()}`;
    const patientName = `${newRequest.patient_first_name} ${newRequest.patient_last_name}`;
    
    // Close modal immediately
    setShowModal(false);
    
    // Add a "Processing..." row to the top of the requests list
    const processingRow = {
      id: tempId,
      patient_first_name: newRequest.patient_first_name,
      patient_last_name: newRequest.patient_last_name,
      question: newRequest.question,
      status: 'PROCESSING',
      created_at: new Date().toISOString(),
      isProcessing: true,
    };
    
    setRequests(prev => [processingRow, ...prev]);
    setTotal(prev => prev + 1);
    setProcessingRequests(prev => ({ ...prev, [tempId]: true }));
    
    // Reset form
    setNewRequest({
      patient_first_name: '',
      patient_last_name: '',
      patient_dob: '',
      question: '',
      response_method: 'portal',
    });
    
    // Now make the API call in the background
    try {
      const res = await api.post('/portal/requests/submit', {
        patient_first_name: processingRow.patient_first_name,
        patient_last_name: processingRow.patient_last_name,
        patient_dob: newRequest.patient_dob,
        question: processingRow.question,
        response_method: newRequest.response_method,
      });
      
      const { request_id, webhook_response } = res.data;
      
      // Update the row with the real ID and response
      setRequests(prev => prev.map(req => {
        if (req.id === tempId) {
          const webhookStatus = webhook_response?.status?.toLowerCase();
          
          if (webhookStatus === 'success') {
            // COMPLETED: Got actual response data
            return {
              ...req,
              id: request_id,
              status: 'COMPLETED',
              isProcessing: false,
              webhookResponse: webhook_response,
            };
          } else if (webhookStatus === 'submitted') {
            // SUBMITTED: Workflow received it but no response data
            return {
              ...req,
              id: request_id,
              status: 'SUBMITTED',
              isProcessing: false,
              submittedMessage: webhook_response.message || 'Request submitted to workflow',
            };
          } else if (webhookStatus === 'error') {
            // ERROR: Got an error response
            const errorCode = webhook_response.error_code;
            let status = 'ERROR';
            if (errorCode === 'ACCESS_DENIED') status = 'ACCESS DENIED';
            else if (errorCode === 'PATIENT_NOT_FOUND') status = 'NOT FOUND';
            
            // Show error popup
            setTimeout(() => {
              setErrorPopup({
                code: errorCode,
                message: webhook_response.message || 'An error occurred',
                patientName,
              });
            }, 100);
            
            return {
              ...req,
              id: request_id,
              status,
              isProcessing: false,
              errorMessage: webhook_response.message,
              errorCode,
            };
          } else {
            // Unknown status - treat as SUBMITTED
            return {
              ...req,
              id: request_id,
              status: 'SUBMITTED',
              isProcessing: false,
              submittedMessage: webhook_response?.message || 'Request submitted',
            };
          }
        }
        return req;
      }));
      
      setProcessingRequests(prev => {
        const updated = { ...prev };
        delete updated[tempId];
        return updated;
      });
      
      if (webhook_response?.status === 'success') {
        toast.success(`Results ready for ${patientName}`);
      } else if (webhook_response?.status === 'submitted') {
        toast.info(`Request submitted for ${patientName}`);
      }
      
    } catch (err) {
      console.error("Failed to submit request:", err);
      
      // For network errors or exceptions, mark as SUBMITTED (we don't know if workflow received it)
      let status = 'SUBMITTED';
      let errorMessage = 'Request may have been submitted. Check back later for results.';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        status = 'SUBMITTED';
        errorMessage = 'Request timed out but may still be processing.';
      } else if (err.response?.status === 408) {
        status = 'SUBMITTED';
        errorMessage = 'Request timed out but may still be processing.';
      } else if (err.response?.status >= 500) {
        status = 'ERROR';
        errorMessage = err.response?.data?.detail || 'Server error. Please try again.';
      } else if (err.response?.data?.detail) {
        status = 'ERROR';
        errorMessage = err.response.data.detail;
      }
      
      // Update the row with appropriate status
      setRequests(prev => prev.map(req => {
        if (req.id === tempId) {
          return {
            ...req,
            status,
            isProcessing: false,
            errorMessage,
          };
        }
        return req;
      }));
      
      setProcessingRequests(prev => {
        const updated = { ...prev };
        delete updated[tempId];
        return updated;
      });
      
      // Show error popup
      setErrorPopup({
        code: status === 'TIMEOUT' ? 'TIMEOUT' : 'ERROR',
        message: errorMessage,
        patientName,
      });
    }
  };
  
  const handleViewRequest = (request) => {
    setViewingRequest(request);
  };
  
  const handleCloseView = () => {
    setViewingRequest(null);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <PortalSidebar />

      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="requests-title">Requests</h1>
              <p className="text-slate-500 mt-1">
                {total} total requests
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 h-10 px-4 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              data-testid="new-request-btn"
            >
              <Plus size={18} />
              New Request
            </button>
          </div>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Requests Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : requests.length === 0 ? (
              <div className="p-12 text-center" data-testid="empty-requests">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-2">No requests found</p>
                <p className="text-sm text-slate-400">
                  Submit a request to get patient status information.
                </p>
              </div>
            ) : (
              <>
                <table className="w-full" data-testid="requests-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Patient</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Question</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {requests.map((req) => (
                      <tr 
                        key={req.id} 
                        className={`hover:bg-slate-50 transition-colors ${req.isProcessing ? 'bg-blue-50/50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              req.isProcessing ? 'bg-blue-100' : 'bg-emerald-100'
                            }`}>
                              {req.isProcessing ? (
                                <Loader2 size={16} className="text-blue-600 animate-spin" />
                              ) : (
                                <FileText size={16} className="text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">
                                {req.patient_first_name && req.patient_last_name 
                                  ? `${req.patient_first_name} ${req.patient_last_name}`
                                  : req.title}
                              </p>
                              <p className="text-xs text-slate-400">
                                {req.isProcessing ? 'Processing...' : `#${req.id?.slice(0, 8) || ''}`}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                          {req.question || req.title || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {getStatusIcon(req.status, req.isProcessing)}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(req.status)}`}>
                              {req.isProcessing ? 'Processing...' : req.status || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {req.status === 'COMPLETED' && req.webhookResponse ? (
                            <button
                              onClick={() => handleViewRequest(req)}
                              className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              View
                              <ChevronRight size={16} />
                            </button>
                          ) : req.isProcessing ? (
                            <span className="text-sm text-slate-400">—</span>
                          ) : req.status === 'COMPLETED' ? (
                            <button
                              onClick={() => handleViewRequest(req)}
                              className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              View
                              <ChevronRight size={16} />
                            </button>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Submit Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-request-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Submit a Request</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmitRequest} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Patient First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newRequest.patient_first_name}
                    onChange={(e) => setNewRequest({...newRequest, patient_first_name: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="First name"
                    data-testid="request-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Patient Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newRequest.patient_last_name}
                    onChange={(e) => setNewRequest({...newRequest, patient_last_name: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Last name"
                    data-testid="request-last-name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Patient Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newRequest.patient_dob}
                  onChange={(e) => setNewRequest({...newRequest, patient_dob: e.target.value})}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="request-dob"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newRequest.question}
                  onChange={(e) => setNewRequest({...newRequest, question: e.target.value})}
                  className="w-full h-32 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g., Has this patient been scheduled yet? When is their next appointment?"
                  data-testid="request-question"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Response Method
                </label>
                <select
                  value={newRequest.response_method}
                  onChange={(e) => setNewRequest({...newRequest, response_method: e.target.value})}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="request-response-method"
                >
                  <option value="portal">Show in Portal</option>
                  <option value="email">Send Email</option>
                  <option value="both">Both</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
                  data-testid="submit-request-btn"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* View Response Modal */}
      {viewingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="view-response-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Patient Status</h2>
                  <p className="text-sm text-slate-500">
                    {viewingRequest.patient_first_name} {viewingRequest.patient_last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseView}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Patient Info */}
              {viewingRequest.webhookResponse?.patient && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2 text-slate-600">
                      <User size={16} />
                      <span className="font-medium">{viewingRequest.webhookResponse.patient.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <Calendar size={16} />
                      <span>DOB: {viewingRequest.webhookResponse.patient.dob}</span>
                    </div>
                    {viewingRequest.webhookResponse.patient.case_status && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        viewingRequest.webhookResponse.patient.case_status === 'Active' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {viewingRequest.webhookResponse.patient.case_status}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Answer */}
              {(viewingRequest.webhookResponse?.answer_html || viewingRequest.webhookResponse?.answers?.[0]) && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Response</h3>
                  {viewingRequest.webhookResponse.answer_html ? (
                    <div 
                      className="text-slate-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingRequest.webhookResponse.answer_html) }}
                    />
                  ) : (
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {viewingRequest.webhookResponse.answers[0]?.answer}
                    </p>
                  )}
                </div>
              )}
              
              {/* Summary */}
              {viewingRequest.webhookResponse?.summary && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-3">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 block">Visits Completed</span>
                      <span className="font-medium text-slate-900">{viewingRequest.webhookResponse.summary.visits_completed || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Cancellations</span>
                      <span className="font-medium text-slate-900">{viewingRequest.webhookResponse.summary.cancellations || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">No Shows</span>
                      <span className="font-medium text-slate-900">{viewingRequest.webhookResponse.summary.no_shows || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Lien Signed</span>
                      <span className={`font-medium ${
                        viewingRequest.webhookResponse.summary.lien_signed === 'YES' ? 'text-emerald-600' : 'text-amber-600'
                      }`}>
                        {viewingRequest.webhookResponse.summary.lien_signed || 'NO'}
                      </span>
                    </div>
                  </div>
                  
                  {viewingRequest.webhookResponse.summary.treating_clinic && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500 block">Treating Clinic</span>
                          <span className="font-medium text-slate-900">{viewingRequest.webhookResponse.summary.treating_clinic}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Therapy Type</span>
                          <span className="font-medium text-slate-900">{viewingRequest.webhookResponse.summary.therapy_type || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Recent Events */}
                  {viewingRequest.webhookResponse.summary.recent_events?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="font-medium text-slate-900 mb-2">Recent Activity</h4>
                      <div className="space-y-2">
                        {viewingRequest.webhookResponse.summary.recent_events.map((event, idx) => (
                          <div key={`${event.date}-${event.status}-${idx}`} className="flex items-start gap-3 text-sm">
                            <span className="text-slate-400 whitespace-nowrap">{event.date}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              event.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                              event.status === 'No Show' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {event.status}
                            </span>
                            {event.reason && <span className="text-slate-600">{event.reason}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {viewingRequest.webhookResponse?.email_sent && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle size={14} className="text-emerald-500" />
                  Email sent to your inbox
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={handleCloseView}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleCloseView();
                  setShowModal(true);
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Popup */}
      {errorPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                errorPopup.code === 'ACCESS_DENIED' ? 'bg-red-100' :
                errorPopup.code === 'PATIENT_NOT_FOUND' ? 'bg-amber-100' :
                errorPopup.code === 'TIMEOUT' ? 'bg-slate-100' :
                'bg-red-100'
              }`}>
                {errorPopup.code === 'ACCESS_DENIED' ? (
                  <ShieldX size={24} className="text-red-600" />
                ) : errorPopup.code === 'PATIENT_NOT_FOUND' ? (
                  <UserX size={24} className="text-amber-600" />
                ) : errorPopup.code === 'TIMEOUT' ? (
                  <AlertTriangle size={24} className="text-slate-600" />
                ) : (
                  <AlertCircle size={24} className="text-red-600" />
                )}
              </div>
              
              <h2 className="text-lg font-semibold text-slate-900 mb-2">
                {errorPopup.code === 'ACCESS_DENIED' ? 'Access Denied' :
                 errorPopup.code === 'PATIENT_NOT_FOUND' ? 'Patient Not Found' :
                 errorPopup.code === 'TIMEOUT' ? 'Request Timeout' :
                 'Unable to Process Request'}
              </h2>
              
              <p className="text-slate-600 mb-2">{errorPopup.message}</p>
              
              {errorPopup.code === 'ACCESS_DENIED' && (
                <p className="text-sm text-slate-500 mb-4">
                  Your firm may not be authorized to access records for {errorPopup.patientName}.
                </p>
              )}
              
              {errorPopup.code === 'PATIENT_NOT_FOUND' && (
                <p className="text-sm text-slate-500 mb-4">
                  Please verify the patient's name and date of birth are correct.
                </p>
              )}
              
              {errorPopup.code === 'TIMEOUT' && (
                <p className="text-sm text-slate-500 mb-4">
                  The server took too long to respond. Please try again.
                </p>
              )}
              
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={() => setErrorPopup(null)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setErrorPopup(null);
                    setShowModal(true);
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
